import { CheckCircle, DollarSign, Download, RefreshCw, Search, XCircle } from "lucide-react";
import React from "react";
import Button from "../../../../components/Button";
import ResponsiveActionButton from "../../../../components/ResponsiveActionButton";
import { useTheme } from "../../../../contexts/ThemeContext";

interface EssentialContractsToolbarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectionSize: number;
  loading: boolean;
  bulkActionLoading: boolean;
  fetchingAllPrices: boolean;
  hasContracts: boolean;
  onRefresh: () => void;
  onFetchAllPrices: () => void;
  onVerifySelected: () => void;
  onUnverifySelected: () => void;
  onVerifyMatches: () => void;
  onDownloadCSV: () => void;
  downloading: boolean;
}

const EssentialContractsToolbar: React.FC<EssentialContractsToolbarProps> = ({
  searchQuery,
  onSearchChange,
  selectionSize,
  loading,
  bulkActionLoading,
  fetchingAllPrices,
  hasContracts,
  onRefresh,
  onFetchAllPrices,
  onVerifySelected,
  onUnverifySelected,
  onVerifyMatches,
  onDownloadCSV,
  downloading,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const someSelected = selectionSize > 0;

  return (
    <div className="flex flex-col gap-4 mb-4">
      {/* Top Actions Row */}
      <div className="flex items-center justify-end gap-2">
        <ResponsiveActionButton
          label="Refresh"
          icon={<RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />}
          onClick={onRefresh}
          disabled={loading}
          variant="default"
        />
        <ResponsiveActionButton
          label={fetchingAllPrices ? "Fetching..." : "Fetch Prices (Page)"}
          icon={<DollarSign className={`w-4 h-4 ${fetchingAllPrices ? "animate-pulse" : ""}`} />}
          onClick={onFetchAllPrices}
          disabled={fetchingAllPrices || !hasContracts}
          variant="default"
        />
        <ResponsiveActionButton
          label="Verify Matches"
          icon={<CheckCircle className="w-4 h-4 text-green-500" />}
          onClick={onVerifyMatches}
          disabled={bulkActionLoading || !hasContracts}
          variant="default"
        />
        <ResponsiveActionButton
          label={downloading ? "Downloading..." : "Download CSV"}
          icon={downloading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          onClick={onDownloadCSV}
          disabled={downloading || !hasContracts}
          variant="default"
        />
      </div>

      {/* Controls Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0">
        {/* Search */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search
              className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                isDarkMode ? "text-rp-grey/50" : "text-gray-500"
              }`}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search symbol, name, ISIN, exchange..."
              className={`w-full pl-10 pr-4 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 ${
                isDarkMode
                  ? "focus:ring-rp-amber bg-rp-surface border-rp-separator text-rp-grey placeholder-rp-grey/40"
                  : "focus:ring-blue-500 bg-white border-gray-300 text-gray-900 placeholder-gray-500"
              }`}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Bulk Actions */}
          {someSelected && (
            <div
              className={`flex items-center gap-3 px-3 py-2 rounded-md ${
                isDarkMode ? "bg-rp-surface border border-rp-separator" : "bg-white border border-gray-200"
              }`}
            >
              <span className={`text-sm font-medium ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}>
                {selectionSize} selected
              </span>
              <div className="h-4 w-px bg-gray-500/30 mx-1" />
              <Button
                onClick={onVerifySelected}
                disabled={bulkActionLoading}
                variant="ghost"
                className={`gap-2 ${
                  isDarkMode
                    ? "bg-green-900/30 text-green-400 hover:bg-green-900/50 border border-green-900/50"
                    : "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                }`}
                size="sm"
              >
                <CheckCircle className="w-4 h-4" />
                Verify
              </Button>
              <Button
                onClick={onUnverifySelected}
                disabled={bulkActionLoading}
                variant="ghost"
                className={`gap-2 ${
                  isDarkMode
                    ? "bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50 border border-yellow-900/50"
                    : "bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200"
                }`}
                size="sm"
              >
                <XCircle className="w-4 h-4" />
                Unverify
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EssentialContractsToolbar;
